/**
 * GridFillerCards — render N "marketing filler" cards to pad a grid so the
 * remaining empty cells aren't visually blank. Each filler card focuses on a
 * single keyword + tagline relevant to where it's placed.
 *
 * Usage:
 *   <GridFillerCards count={2} kind="survey" startIndex={11} />
 *
 *   count       = how many filler cards to render (the empty cells in the last row)
 *   kind        = "survey" | "cad"        — which keyword pool to draw from
 *   startIndex  = the # to print on the corner of the first filler card
 */
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const SURVEY_FILLERS = [
  {
    emoji: "🛡️",
    title: "3-Tier QC",
    body: "Drafter → senior reviewer → licensed surveyor on every drawing. Catch errors before your PLS signs.",
    keyword: "Survey-licensed QC",
    cta: { to: "/about", label: "Our QC process" },
  },
  {
    emoji: "⏱️",
    title: "24-Hour Turnaround",
    body: "Overnight production aligned to USA, Canada and Australia timezones. First drafts on your desk by morning.",
    keyword: "Overnight production",
    cta: { to: "/contact", label: "Request quote" },
  },
  {
    emoji: "🌎",
    title: "USA · Canada · Australia",
    body: "All 50 states, 13 Canadian provinces and 8 Australian regions — your survey crews stay focused on the field.",
    keyword: "Multi-region drafting",
    cta: { to: "/survey-techies", label: "Embed a techie" },
  },
  {
    emoji: "🔐",
    title: "NDA-Ready",
    body: "Mutual NDA, encrypted file transfer, 60-day retention. Your CAD IP and client data never leave our secure pipeline.",
    keyword: "Secure CAD pipeline",
    cta: { to: "/about", label: "Security overview" },
  },
];

const CAD_FILLERS = [
  {
    emoji: "🏗️",
    title: "AEC + Mechanical",
    body: "Architecture, structural detailing, MEP/BIM coordination, mechanical, electrical — one bench, every discipline.",
    keyword: "Multi-discipline CAD",
    cta: { to: "/cad-techies", label: "CAD Techies" },
  },
  {
    emoji: "📐",
    title: "Your CAD Standards",
    body: "We ingest your templates, blocks, layer standards and plot styles on day one — every drawing matches your firm's DNA.",
    keyword: "Standards-aligned",
    cta: { to: "/contact", label: "Send your template" },
  },
  {
    emoji: "💲",
    title: "Transparent Pricing",
    body: "Hourly, per-project, or dedicated-FTE billing. No hidden seat licenses, no surprise line items, no markup games.",
    keyword: "Predictable pricing",
    cta: { to: "/contact", label: "Get a quote" },
  },
  {
    emoji: "🧩",
    title: "Embed Like In-House",
    body: "Direct Slack/Teams channel, shared project tracker, named drafter. Your team — without the HR overhead.",
    keyword: "Embedded talent",
    cta: { to: "/services", label: "See all services" },
  },
];

export default function GridFillerCards({ count, kind = "survey", startIndex = 0, total = null }) {
  const pool = kind === "cad" ? CAD_FILLERS : SURVEY_FILLERS;
  // NOTE: if `count` ever exceeds pool length (4) the same filler will repeat.
  // In practice the lg:grid-cols-3 layout never asks for more than 2 fillers,
  // so cycling is safe — but bump the pools if you change the column count.
  const fillers = Array.from({ length: Math.max(0, count) }, (_, i) => pool[i % pool.length]);
  return fillers.map((f, i) => {
    const idx = startIndex + i + 1;
    const label = total ? `${String(idx).padStart(2, "0")} / ${total}` : String(idx).padStart(2, "0");
    return (
      <div
        key={`filler-${kind}-${i}`}
        data-testid={`grid-filler-${kind}-${i}`}
        className="bg-paper-dark/30 p-6 lg:p-7 flex flex-col gap-4 min-h-[220px] relative border-t border-slate-200/40 sm:border-t-0"
      >
        <div className="flex items-center justify-between">
          <span className="text-3xl leading-none">{f.emoji}</span>
          <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-400">
            {label}
          </span>
        </div>
        <div>
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-orange-survey">
            {f.keyword}
          </div>
          <div className="display mt-1.5 text-lg sm:text-xl font-extrabold tracking-tight text-slate-900">
            {f.title}
          </div>
          <div className="mt-2 text-sm text-slate-600 leading-relaxed">{f.body}</div>
        </div>
        <Link
          to={f.cta.to}
          className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-topo hover:text-orange-survey transition-colors"
        >
          {f.cta.label} <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    );
  });
}
